import { useMemo } from "react";

import { useQuery } from "@apollo/client";

import useMe from "hooks/useMe";

import getUserName from "utils/getUserName";

import ORGANIZATION_MEMBERS_QUERY from "@gql/queries/organization/organizationMembers";

import { parseLawyerData } from "./parseLawyerData";

export const useOrgLawyers = ({ entityObject, values, setValues, userId }) => {
  const { me } = useMe();

  const { data, loading } = useQuery(ORGANIZATION_MEMBERS_QUERY, {
    variables: { where: { userId } },
    skip: !userId || !entityObject?.isOurClient,
    /*
    The no-cache fetch policy is set because Apollo cache conflicts between
    me.user.professional and organization.members.user.professional
    due to the shared __typename: 'User', and it automatically refetch the me query.
    
    TODO: Find the proper way to update these objects in the cache.
    */
    fetchPolicy: "no-cache",
  });

  const orgName = useMemo(() => data?.organization?.name, [data]);

  const orgLawyers = useMemo(
    () =>
      (data?.organization?.members || []).filter(
        (member) => member?.user?.professional?.type === "LAWYER",
      ),
    [data],
  );

  const lawyers = useMemo(
    () =>
      orgLawyers.map((member) => ({
        label: getUserName(member.user) || member.user?.professional?.profile?.email,
        value: getUserName(member.user) || member.user?.professional?.profile?.email,
      })) || [],
    [orgLawyers],
  );

  const selectValue = useMemo(() => {
    if (loading) return undefined;

    return values?.name || entityObject?.name || undefined;
  }, [loading, values?.name, entityObject]);

  const handleSelectChange = (value) => {
    const selectedLawyer = orgLawyers.find((lawyer) => getUserName(lawyer.user) === value.value);

    const orgLocations = me?.professional?.organization?.locations || [];

    setValues({
      ...values,
      ...parseLawyerData(selectedLawyer, orgName, orgLocations),
      sanityId: "",
    });
  };

  return { loading, lawyers, selectValue, handleSelectChange };
};
